import React from 'react';
import { Box, Typography } from '@mui/material';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';

interface DetailScoresProps {
  scoreVisual: number | null;
  scoreSound: number | null;
  scoreGameplay: number | null;
  scoreNarrative: number | null;
  scoreOriginality: number | null;
  variant?: 'reviewItem' | 'reviewsTab';
  hideBarOnDesktop?: boolean;
}

const DetailScores: React.FC<DetailScoresProps> = ({
  scoreVisual,
  scoreSound,
  scoreGameplay,
  scoreNarrative,
  scoreOriginality,
  variant = 'reviewItem',
  hideBarOnDesktop = false
}) => {
  const getScoreColor = (score: number | null) => {
    if (score === null) return "#666666";
    if (score >= 4.5) return "#4CAF50";
    if (score >= 4.0) return "#8BC34A";
    if (score >= 3.5) return "#CDDC39";
    if (score >= 3.0) return "#FFEB3B";
    if (score >= 2.5) return "#FFC107";
    if (score >= 2.1) return "#FF9800";
    return "#F44336";
  };

  const scores = [
    { key: 'score_visual', label: 'VIS', fullLabel: 'Visual', icon: PaletteOutlinedIcon, value: scoreVisual },
    { key: 'score_sound', label: 'SND', fullLabel: 'Sound', icon: VolumeUpIcon, value: scoreSound },
    { key: 'score_gameplay', label: 'PLY', fullLabel: 'Gameplay', icon: SportsEsportsIcon, value: scoreGameplay },
    { key: 'score_narrative', label: 'NAR', fullLabel: 'Narrative', icon: MenuBookOutlinedIcon, value: scoreNarrative },
    { key: 'score_originality', label: 'ORG', fullLabel: 'Originality', icon: AutoAwesomeOutlinedIcon, value: scoreOriginality }
  ];

  // すべてのスコアが未評価の場合は何も表示しない
  const hasAnyScore = scores.some(score => score.value !== null);
  if (!hasAnyScore) {
    return null;
  }

  if (variant === 'reviewsTab') {
    // ReviewsTab用の表示
    return (
      <>
        {/* PC用詳細スコア表示 */}
        <Box sx={{ 
          display: { xs: 'none', sm: 'flex' },
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 0.75,
          px: 1.5,
          bgcolor: 'rgba(255, 255, 255, 0.02)',
          borderRadius: 1,
          border: '1px solid rgba(255, 255, 255, 0.06)',
          mb: 0.5
        }}>
          {scores.map((item) => (
            <Box key={item.key} sx={{ 
              display: 'flex',
              alignItems: 'center',
              gap: 0.2,
              minWidth: 0,
              flex: 1
            }}>
              <item.icon sx={{ 
                fontSize: '0.8rem', 
                color: 'rgba(255, 255, 255, 0.6)', 
                marginRight: '0.1rem'
              }} />
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: '0.6rem',
                  color: 'rgba(255, 255, 255, 0.6)',
                  lineHeight: 1,
                  fontWeight: 400,
                  letterSpacing: '0.3px',
                  mr: 0.5
                }}
              >
                {item.label}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.6rem',
                  fontWeight: 500,
                  color: item.value !== null ? getScoreColor(item.value) : 'rgba(255, 255, 255, 0.3)',
                  lineHeight: 1,
                  mr: 0.5
                }}
              >
                {item.value !== null ? item.value.toFixed(1) : '—'}
              </Typography>
              {!hideBarOnDesktop && (
                <Box
                  sx={{
                    width: 24,
                    height: 2.5,
                    bgcolor: "rgba(255, 255, 255, 0.25)",
                    borderRadius: 1.5,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: item.value !== null ? `${((item.value - 1) / 4) * 100}%` : '0%',
                      height: "100%",
                      bgcolor: item.value !== null ? getScoreColor(item.value) : 'transparent',
                      borderRadius: 1.5,
                      transition: "width 0.3s ease",
                    }}
                  />
                </Box>
              )}
            </Box>
          ))}
        </Box>

        {/* モバイル用詳細スコア表示 */}
        <Box sx={{ 
          display: { xs: 'block', sm: 'none' },
          mb: 0.5
        }}>
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: 1,
            p: 0.5,
            bgcolor: 'rgba(255, 255, 255, 0.02)',
            borderRadius: 1,
            border: '1px solid rgba(255, 255, 255, 0.06)'
          }}>
            {scores.map((item) => (
              <Box key={item.key} sx={{ 
                textAlign: 'center',
                minWidth: 0
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5, 
                  justifyContent: 'center',
                  mb: 0.5 
                }}>
                  <item.icon sx={{ 
                    fontSize: '0.8rem', 
                    color: 'rgba(255, 255, 255, 0.6)' 
                  }} />
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: '0.65rem',
                      color: 'rgba(255, 255, 255, 0.6)',
                      lineHeight: 1,
                      fontWeight: 400,
                      letterSpacing: '0.5px'
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  gap: 0.5 
                }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 500,
                      color: item.value !== null ? getScoreColor(item.value) : 'rgba(255, 255, 255, 0.3)',
                      lineHeight: 1
                    }}
                  >
                    {item.value !== null ? item.value.toFixed(1) : '—'}
                  </Typography>
                  <Box
                    sx={{
                      width: 20,
                      height: 3,
                      bgcolor: "rgba(255, 255, 255, 0.25)",
                      borderRadius: 1.5,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: item.value !== null ? `${((item.value - 1) / 4) * 100}%` : '0%',
                        height: "100%",
                        bgcolor: item.value !== null ? getScoreColor(item.value) : 'transparent',
                        borderRadius: 1.5,
                        transition: "width 0.3s ease",
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </>
    );
  }

  // ReviewItem用の表示
  return (
    <>
      {/* PC用詳細スコア表示 */}
      <Box sx={{ 
        display: { xs: 'none', sm: 'flex' },
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 0.75,
        px: 1.5,
        bgcolor: 'rgba(255, 255, 255, 0.02)',
        borderRadius: 1,
        border: '1px solid rgba(255, 255, 255, 0.06)'
      }}>
        {scores.map((item) => (
          <Box key={item.key} sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 0.2,
            minWidth: 0,
            flex: 1
          }}>
            <item.icon sx={{ 
              fontSize: '0.8rem', 
              color: 'rgba(255, 255, 255, 0.6)', 
              marginRight: '0.1rem'
            }} />
            <Typography 
              variant="caption" 
              sx={{ 
                fontSize: '0.6rem',
                color: 'rgba(255, 255, 255, 0.6)',
                lineHeight: 1,
                fontWeight: 400,
                letterSpacing: '0.3px',
                mr: 0.5
              }}
            >
              {item.label}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.6rem',
                fontWeight: 500,
                color: item.value !== null ? getScoreColor(item.value) : 'rgba(255, 255, 255, 0.3)',
                lineHeight: 1,
                mr: 0.5
              }}
            >
              {item.value !== null ? item.value.toFixed(1) : '—'}
            </Typography>
            <Box
              sx={{
                width: 24,
                height: 2.5,
                bgcolor: "rgba(255, 255, 255, 0.25)",
                borderRadius: 1.5,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: item.value !== null ? `${((item.value - 1) / 4) * 100}%` : '0%',
                  height: "100%",
                  bgcolor: item.value !== null ? getScoreColor(item.value) : 'transparent',
                  borderRadius: 1.5,
                  transition: "width 0.3s ease",
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>

      {/* モバイル用詳細スコア表示 */}
      <Box sx={{ 
        display: { xs: 'block', sm: 'none' },
      }}>
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: 1,
          p: 0.5,
          bgcolor: 'rgba(255, 255, 255, 0.02)',
          borderRadius: 1,
          border: '1px solid rgba(255, 255, 255, 0.06)'
        }}>
          {scores.map((item) => (
            <Box key={item.key} sx={{ 
              textAlign: 'center',
              minWidth: 0
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5, 
                justifyContent: 'center',
                mb: 0.5 
              }}>
                <item.icon sx={{ 
                  fontSize: '0.8rem', 
                  color: 'rgba(255, 255, 255, 0.6)' 
                }} />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.65rem',
                    color: 'rgba(255, 255, 255, 0.6)',
                    lineHeight: 1,
                    fontWeight: 400,
                    letterSpacing: '0.5px'
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                gap: 0.5 
              }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.65rem',
                    fontWeight: 500,
                    color: item.value !== null ? getScoreColor(item.value) : 'rgba(255, 255, 255, 0.3)',
                    lineHeight: 1
                  }}
                >
                  {item.value !== null ? item.value.toFixed(1) : '—'}
                </Typography>
                <Box
                  sx={{
                    width: 20,
                    height: 3,
                    bgcolor: "rgba(255, 255, 255, 0.25)",
                    borderRadius: 1.5,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: item.value !== null ? `${((item.value - 1) / 4) * 100}%` : '0%',
                      height: "100%",
                      bgcolor: item.value !== null ? getScoreColor(item.value) : 'transparent',
                      borderRadius: 1.5,
                      transition: "width 0.3s ease",
                    }}
                  />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </>
  );
};

export default DetailScores;